import { watch, ref } from 'vue';
import { checkUpdateWhenNoSW, checkVersionTagUpdate, isNativeApp, VersionTagStorage } from '@golden/app-plugins';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import thisVersion, { getLatestVersionParams, getVersionTagParams } from 'virtual:golden/version';
import { useWatchObservable } from './useWatchObservable';
import { filter, fromEvent } from 'rxjs';
const intervalMS = 1 * 60 * 1000;
const useNoSWUpdateVersion = (gdk, url) => {
    const isNeedRefresh = ref(false);
    const subscription = checkUpdateWhenNoSW(gdk, getLatestVersionParams(url), thisVersion).subscribe((data) => { isNeedRefresh.value = !data; });
    const close = () => {
        if (!subscription.closed)
            subscription.unsubscribe();
    };
    return { isNeedRefresh, close };
};
export const useUpdateVersion = (gdk, url) => {
    const { close: closeUpdate, isNeedRefresh } = useNoSWUpdateVersion(gdk, url);
    const isSWNeedRefresh = ref(false);
    const isVersionTagUpdate = ref(false);
    const versionTagUpdateProgress = ref(0);
    const interval = ref(null);
    const { offlineReady, updateServiceWorker } = useRegisterSW({
        immediate: !isNativeApp(),
        onOfflineReady() {
            // eslint-disable-next-line no-console
            console.log('onOfflineReady');
            closeUpdate();
        },
        onRegistered(r) {
            if (!r)
                return;
            // eslint-disable-next-line no-console
            console.log('onRegistered');
            checkVersionTagUpdate({
                r,
                gdk,
                params: getVersionTagParams(url),
                onUpdateFound: (newWorker) => {
                    console.log('onUpdateTagFound');
                    if (newWorker) {
                        fromEvent(newWorker, 'statechange').subscribe(() => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                isSWNeedRefresh.value = true;
                            }
                        });
                    }
                    if (isSWNeedRefresh.value)
                        isSWNeedRefresh.value = false;
                    if (versionTagUpdateProgress.value === 0)
                        isVersionTagUpdate.value = true;
                    if (interval.value)
                        clearInterval(interval.value);
                    versionTagUpdateProgress.value = 0;
                    let step = 0.03;
                    let progress = 0;
                    interval.value = window.setInterval(() => {
                        if (isSWNeedRefresh.value) {
                            versionTagUpdateProgress.value = 100;
                            clearInterval(interval.value);
                            return;
                        }
                        if (versionTagUpdateProgress.value >= 97) {
                            return;
                        }
                        if (versionTagUpdateProgress.value >= 85) {
                            step = 0.01;
                        }
                        if (versionTagUpdateProgress.value >= 70) {
                            step = 0.02;
                        }
                        progress += step;
                        versionTagUpdateProgress.value = Math.round(Math.atan(progress) / (Math.PI / 2) * 100 * 1000) / 1000;
                    }, 100);
                },
                canRefresh: () => useWatchObservable(isSWNeedRefresh, { immediate: true }).pipe(filter((v) => v.newVal))
            }).subscribe((tag) => {
                VersionTagStorage.getInstance().versionTag = tag;
            });
            void r.update();
            setInterval(() => { void r.update(); }, intervalMS);
        },
        onNeedRefresh() {
            console.log('onNeedRefresh');
            isSWNeedRefresh.value = true;
        }
    });
    const triggerUpdate = (callback) => {
        if (versionTagUpdateProgress.value > 0 && versionTagUpdateProgress.value < 100)
            return;
        if (isSWNeedRefresh.value) {
            void updateServiceWorker(true);
            isSWNeedRefresh.value = false;
        }
        if (isNeedRefresh.value) {
            callback();
            isVersionTagUpdate.value = false;
        }
    };
    watch(offlineReady, (value) => {
        if (!value)
            return;
        // eslint-disable-next-line no-console
        console.log('App ready to work offline');
    });
    return {
        isSWNeedRefresh,
        isNeedRefresh,
        isVersionTagUpdate,
        versionTagUpdateProgress,
        triggerUpdate
    };
};
//# sourceMappingURL=useUpdateVersion.js.map